import { useCallback, useEffect, useState } from 'react';
import type { Specialty, SpecialtyAlias } from '@starter/shared';
import { useProgramId } from '../../auth/AuthContext.js';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { Modal } from '../../components/Modal.js';
import { PageHeader } from '../../components/PageHeader.js';
import { StatusBadge } from '../../components/StatusBadge.js';

type FormMode = 'create' | 'edit' | null;

export function SpecialtiesPage() {
  const programId = useProgramId();
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<FormMode>(null);
  const [editing, setEditing] = useState<Specialty | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [aliases, setAliases] = useState<SpecialtyAlias[]>([]);
  const [newAlias, setNewAlias] = useState('');

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive'>('active');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .listSpecialties({ programId, page: 1, pageSize: 100 })
      .then((res) => setSpecialties(res.data))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load specialties'))
      .finally(() => setLoading(false));
  }, [programId]);

  useEffect(load, [load]);

  function openCreate() {
    setEditing(null);
    setName('');
    setCode('');
    setStatus('active');
    setAliases([]);
    setNewAlias('');
    setFormError(null);
    setMode('create');
  }

  function openEdit(specialty: Specialty) {
    setEditing(specialty);
    setName(specialty.name);
    setCode(specialty.code);
    setStatus(specialty.status);
    setAliases([]);
    setNewAlias('');
    setFormError(null);
    setMode('edit');
  }

  function closeModal() {
    setMode(null);
    setEditing(null);
    setFormError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      if (mode === 'create') {
        await api.createSpecialty({ programId, name, code: code.toUpperCase() });
      } else if (mode === 'edit' && editing) {
        await api.updateSpecialty(editing.id, { name, status });
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddAlias() {
    if (!editing || !newAlias.trim()) return;
    setFormError(null);
    try {
      const created = await api.createSpecialtyAlias(editing.id, { alias: newAlias.trim() });
      setAliases((prev) => [...prev, created]);
      setNewAlias('');
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to add alias');
    }
  }

  async function handleRemoveAlias(alias: SpecialtyAlias) {
    if (!editing) return;
    try {
      await api.deleteSpecialtyAlias(editing.id, alias.id);
      setAliases((prev) => prev.filter((a) => a.id !== alias.id));
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to remove alias');
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Specialties"
        subtitle="Surgical specialties and matching aliases"
        actions={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            Add specialty
          </button>
        }
      />

      {error && <ErrorBanner message={error} onRetry={load} />}

      {loading ? (
        <LoadingState message="Loading specialties…" />
      ) : (
        <div className="card">
          {specialties.length === 0 ? (
            <p className="text-muted">No specialties defined yet.</p>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {specialties.map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td>
                        <code>{s.code}</code>
                      </td>
                      <td>
                        <StatusBadge status={s.status} variant={s.status === 'active' ? 'success' : 'muted'} />
                      </td>
                      <td>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => openEdit(s)}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal
        open={mode !== null}
        title={mode === 'create' ? 'Add specialty' : 'Edit specialty'}
        onClose={closeModal}
        footer={
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={closeModal}>
              Cancel
            </button>
            <button type="submit" form="specialty-form" className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <form id="specialty-form" onSubmit={handleSubmit}>
          {formError && <div className="banner banner--error" role="alert">{formError}</div>}
          <label className="field">
            <span className="field__label">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="field">
            <span className="field__label">Code</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              disabled={mode === 'edit'}
            />
          </label>
          {mode === 'edit' && (
            <>
              <label className="field">
                <span className="field__label">Status</span>
                <select value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </label>

              <div className="alias-section">
                <h3 className="alias-section__title">Aliases</h3>
                <p className="text-muted text-sm">
                  Add alternate names for document matching. Aliases added in this session appear below.
                </p>
                <div className="alias-input-row">
                  <input
                    value={newAlias}
                    onChange={(e) => setNewAlias(e.target.value)}
                    placeholder="e.g. general surgery"
                  />
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => void handleAddAlias()}>
                    Add
                  </button>
                </div>
                {aliases.length > 0 && (
                  <ul className="alias-list">
                    {aliases.map((a) => (
                      <li key={a.id}>
                        <span>{a.alias}</span>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm btn--danger"
                          onClick={() => void handleRemoveAlias(a)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </form>
      </Modal>
    </div>
  );
}
